import {useEffect, useState} from "react";
import {deleteRule, getRules, updateRuleToggle} from "@/queries/ruleQueries";

import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton, PaperProps,
    Stack,
    Typography
} from "@mui/material";
import {formatDuration, formatUTCDate, getSubRoot} from "@/utils/utils";
import { enqueueSnackbar } from "notistack";
import Switch from "@mui/material/Switch";
import {useNavigate} from "react-router";

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {AUTOMATION_FORGE} from "@/constants/globalConstants";
import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import DialogContentText from "@mui/material/DialogContentText";
import {ruleCategoriesEnum} from "@/types/ruleTypes";
import {useIntl} from "react-intl";
import { circularIconHoverSx } from "@/styles/iconHover";

export default function RulesPage() {

    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isOpenDelete, setIsOpenDelete] = useState(false);
    const [idToDelete, setIdToDelete] = useState();

    const intl = useIntl();


    const handleDeleteRule = async (id) => {
        const originalRow = rows.find((row) => row.id === id);
        const originalIndex = rows.findIndex((row) => row.id === id);
        let isErrorPresent = false

        setRows((prevRows) => prevRows.filter((row) => row.id !== id));

        try {
            const response = await deleteRule(id);
        }
        catch(error) {
            isErrorPresent = true;
            setRows((prevRows) => {
                const restoredRows = [...prevRows];
                restoredRows.splice(originalIndex, 0, originalRow);
                return restoredRows;
            });

            enqueueSnackbar("Error while deleting Rule.")
        }
        finally {
            if (!isErrorPresent) {
                enqueueSnackbar("Rule Deleted.");
            }
        }
    }

    const handleToggleEnabled = async (id, currentStatus) => {
        setRows((prevRows) =>
            prevRows.map((row) =>
                row.id === id ? { ...row, enabled: !currentStatus } : row
            )
        );

        try {
            setLoading(true);
            const response = await updateRuleToggle(id, !currentStatus);
        } catch (error) {
            console.error("Errore durante l'aggiornamento dello stato:", error);

            // ROLLBACK
            setRows((prevRows) =>
                prevRows.map((row) =>
                    row.id === id ? { ...row, enabled: currentStatus } : row
                )
            );

            enqueueSnackbar("Error updating the current state.")
        }
        finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        const initializeRules = async () => {
            try {
                setLoading(true)
                const response = await getRules()
                setRows(response)
            }
            catch (e) {
                console.error(e)
            }
            finally {
                setLoading(false)
            }
        }
        initializeRules();
    }, []);

    const columnsTrigger: GridColDef[] = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
            minWidth: 250,
        },
        {
            field: 'trigger',
            headerName: 'Trigger',
            width: 150,
            valueGetter: (value, row) =>  {
                if(row.category === ruleCategoriesEnum.MATCH && value === 'on_event_analyzed')
                    return intl.formatMessage({id: value.concat('_match'), defaultMessage: value.concat('_match')})
                else if(row.category === ruleCategoriesEnum.CALENDAR && value === 'on_event_analyzed')
                    return intl.formatMessage({id: value.concat('_calendar'), defaultMessage: value.concat('_calendar')})
                return intl.formatMessage({id: value, defaultMessage: value})
            }
        },
        {
            field: 'scheduler',
            headerName: 'Updated At',
            width: 150,
            valueGetter: (value, row) => {
                if (row.executions && row.executions.length > 0) {
                    return formatUTCDate(row.executions[0].updated_at);
                }
                return "N/A";
            },
        },
        {
            field: 'executions',
            headerName: 'N. Executions',
            width: 100,
            valueGetter: (value, row) => {
                return row.executions.length
            }
        },
        {
            field: 'last_time_triggered',
            headerName: 'Last Time Triggered',
            width: 150,
            valueGetter: (value, row) => {
                if (row.last_time_triggered === null || row.last_time_triggered === undefined) {
                    return "Not triggered yet"
                }
                return formatUTCDate(row.last_time_triggered)
            }
        },
        {
            field: 'last_time',
            headerName: 'Last Response Status',
            width: 170,
            valueGetter: (value, row) => {
                if (row.executions && row.executions.length > 0) {
                    return row.executions[0].rc < 300 ? 'OK' : 'ERROR';
                }
                return "N/A";
            },
            renderCell: (params) => {
                let textColor = 'text.primary';
                if (params.value === 'OK') {
                    textColor = 'success.main';
                } else if (params.value === 'ERROR') {
                    textColor = 'error.main';
                }

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
                        <Typography sx={{ color: textColor, fontWeight: 'bold' }}>
                            {params.value}
                        </Typography>
                    </Box>
                );
            }
        },
        {
            field: 'enabled',
            headerName: 'Enabled Status',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%'
                    }}>
                        <Switch
                            checked={params.value}
                            onChange={() => handleToggleEnabled(params.row.id, params.value)}
                            color="primary"
                            disabled={loading}
                        />
                    </div>
                );
            }
        },
        {
            field: 'id',
            headerName: '',
            width: 150,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        spacing={1}
                        sx={{ width: '100%', height: '100%' }}
                    >
                        {/* Bottone MODIFICA*/}
                        <IconButton
                            data-tour={"rule_edit_button_overview"}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`${getSubRoot(window.location.pathname)}/${AUTOMATION_FORGE}?id=${params.row.id}`)
                            }}
                            sx={circularIconHoverSx()}
                        >
                            <EditOutlinedIcon />
                        </IconButton>

                        {/* Bottone DELETE*/}
                        <IconButton
                            data-tour={"rule_delete_button_overview"}
                            onClick={(e) => {
                                e.stopPropagation();
                                setIdToDelete(params.row.id)
                                setIsOpenDelete(true)
                            }}
                            sx={circularIconHoverSx()}
                        >
                            <DeleteOutlined />
                        </IconButton>
                    </Stack>
                )
            }
        }
    ];

    const columnsTime: GridColDef[] = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
            minWidth: 200,
        },
        {
            field: 'start',
            headerName: 'Start of Rule',
            width: 150,
            valueGetter: (value, row) => {
                return formatUTCDate(row.time_params["start"])
            }
        },
        {
            field: 'period',
            headerName: 'Period',
            width: 100,
            valueGetter: (value, row) =>  {
                    return formatDuration(row.time_params["period"])
            }
        },
        {
            field: 'scheduler',
            headerName: 'Updated At',
            width: 150,
            valueGetter: (value, row) => {
                if (row.executions && row.executions.length > 0) {
                    return formatUTCDate(row.executions[0].updated_at);
                }
                return "N/A";
            },
        },
        {
            field: 'executions',
            headerName: 'N. Executions',
            width: 100,
            valueGetter: (value, row) => {
                return row.executions.length
            }
        },
        {
            field: 'last_time_triggered',
            headerName: 'Last Time Triggered',
            width: 150,
            valueGetter: (value, row) => {
                if (row.last_time_triggered === null || row.last_time_triggered === undefined) {
                    return "Not triggered yet"
                }
                return formatUTCDate(row.last_time_triggered)
            }
        },
        {
            field: 'last_time',
            headerName: 'Last Response Status',
            width: 170,
            valueGetter: (value, row) => {
                if (row.executions && row.executions.length > 0) {
                    return row.executions[0].rc < 300 ? 'OK' : 'ERROR';
                }
                return "N/A";
            },
            renderCell: (params) => {
                let textColor = 'text.primary';
                if (params.value === 'OK') {
                    textColor = 'success.main';
                } else if (params.value === 'ERROR') {
                    textColor = 'error.main';
                }

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
                        <Typography sx={{ color: textColor, fontWeight: 'bold' }}>
                            {params.value}
                        </Typography>
                    </Box>
                );
            }
        },
        {
            field: 'enabled',
            headerName: 'Enabled Status',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%'
                    }}>
                        <Switch
                            checked={params.value}
                            onChange={() => handleToggleEnabled(params.row.id, params.value)}
                            color="primary"
                            disabled={loading}
                        />
                    </div>
                );
            }
        },
        {
            field: 'id',
            headerName: '',
            width: 150,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        spacing={1}
                        sx={{ width: '100%', height: '100%' }}
                    >
                        {/* Bottone MODIFICA*/}
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`${getSubRoot(window.location.pathname)}/${AUTOMATION_FORGE}?id=${params.row.id}`)
                            }}
                            sx={circularIconHoverSx()}
                        >
                            <EditOutlinedIcon />
                        </IconButton>

                        {/* Bottone DELETE*/}
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                setIdToDelete(params.row.id)
                                setIsOpenDelete(true)
                            }}
                            sx={circularIconHoverSx()}
                        >
                            <DeleteOutlined />
                        </IconButton>
                    </Stack>
                )
            }
        }
    ];



    return (
        <Box>
            <Button data-tour={"rule_create_new_button"} variant="contained" onClick={() => navigate(getSubRoot(window.location.pathname) + "/" + AUTOMATION_FORGE)}>Create New Rule</Button>

            <Typography variant={"h4"} sx={{py:1, my:2}}>Event-Based Rules</Typography>
            <Box data-tour={"rule_event_based_table"} sx={{ width: '100%', overflowX: 'auto' }}>
                <DataGrid
                    rows={rows.filter(value => value.scheduler === 'trigger-based')}
                    columns={columnsTrigger}
                    loading={loading}
                    sx={{ minWidth: 1050 }}
                />
            </Box>

            <Typography variant={"h4"} sx={{py:1, my:2}}>Time-Based Rules</Typography>
            <Box data-tour={"rule_time_based_table"} sx={{ width: '100%', overflowX: 'auto' }}>
                <DataGrid
                    rows={rows.filter(value => value.scheduler === 'time-based')}
                    columns={columnsTime}
                    loading={loading}
                    sx={{ minWidth: 1050 }}
                />
            </Box>

            <Dialog
                open={isOpenDelete}
                onClose={() => setIsOpenDelete(false)}
                fullWidth
                maxWidth="sm"
                PaperComponent={(props: PaperProps) => (
                    <Box
                        sx={{
                            height: 550,
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            pointerEvents: 'none',
                            mt: 10,
                        }}
                    >
                        <Box
                            {...props}
                            sx={{
                                ...props.sx,
                                boxShadow: 6,
                                pointerEvents: 'auto',
                                backgroundColor: 'grey.100',
                                margin: 0,
                                width: '100%',
                                maxWidth: '640px',
                            }}
                        >
                            <Box
                                sx={{
                                    backgroundColor: 'grey.100',
                                    borderRadius: 1.5,
                                    p: 1,
                                }}
                            >
                                {props.children}
                            </Box>
                        </Box>
                    </Box>
                )}
            >
                {/* Contenuto del Dialog */}
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    Conferma Eliminazione
                </DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        Are you sure to delete this rule? This operation cannot be undone.
                    </DialogContentText>
                </DialogContent>

                <DialogActions sx={{ padding: 2, gap: 1 }}>
                    <Button
                        onClick={() => setIsOpenDelete(false)}
                        color="secondary"
                        variant="outlined"
                        size="large"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            handleDeleteRule(idToDelete);
                            setIsOpenDelete(false);
                        }}
                        color="primary"
                        variant="contained"
                        size="large"
                        disableElevation
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}